import { useCallback, useEffect, useState } from 'react';
import tprmService from '../../../services/compliance/tprm.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import { VendorOffboardingTask } from '../../../types/compliance';
import PanelNotifications, {
  usePanelNotifications,
} from '../../../components/compliance/PanelNotifications';

interface Props {
  vendorId: string;
}

export default function OffboardingPanel({ vendorId }: Props) {
  const { isManager: canEdit } = useComplianceAccess();
  // Destructured so `notifyFailure` is a stable ref (useCallback([]))
  // rather than a shifting member of a freshly-rebuilt object — see the
  // RiskAcceptancesPanel comment for the infinite-loop rationale.
  const {
    error: toastError,
    success: toastSuccess,
    clear: clearToasts,
    notifyFailure,
  } = usePanelNotifications();

  const [tasks, setTasks] = useState<VendorOffboardingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tprmService.listOffboardingTasks(vendorId);
      setTasks(rows);
      const drafts: Record<string, string> = {};
      rows.forEach((r) => { drafts[r.id] = r.notes ?? ''; });
      setNotesDraft(drafts);
    } catch (error) {
      notifyFailure('Failed to load offboarding tasks', error);
    } finally {
      setLoading(false);
    }
  }, [vendorId, notifyFailure]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(
    async (task: VendorOffboardingTask, completed: boolean) => {
      setBusyId(task.id);
      try {
        const updated = await tprmService.updateOffboardingTask(
          vendorId,
          task.id,
          { completed, notes: notesDraft[task.id] ?? task.notes ?? undefined },
        );
        setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
      } catch (error) {
        notifyFailure('Failed to update task', error);
      } finally {
        setBusyId(null);
      }
    },
    [vendorId, notesDraft, notifyFailure],
  );

  if (loading) {
    return <div className="text-sm text-gray-500">Loading offboarding tasks…</div>;
  }

  if (tasks.length === 0) {
    return (
      <div className="text-sm text-muted">
        No offboarding tasks yet. Archive the vendor to auto-generate the checklist.
      </div>
    );
  }

  const completedCount = tasks.filter((t) => t.completed).length;

  return (
    <div className="space-y-4">
      <PanelNotifications
        error={toastError}
        success={toastSuccess}
        onClearError={clearToasts}
        onClearSuccess={clearToasts}
      />
      <div className="text-sm text-gray-600 dark:text-gray-300">
        {completedCount} of {tasks.length} tasks complete.
      </div>
      <ul className="space-y-2">
        {tasks.map((t) => (
          <li
            key={t.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-panel"
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={t.completed}
                disabled={!canEdit || busyId === t.id}
                onChange={(e) => toggle(t, e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <div className="flex-1">
                <div
                  className={`font-medium ${
                    t.completed
                      ? 'line-through text-muted'
                      : 'text-body'
                  }`}
                >
                  {t.title}
                </div>
                {t.completed && t.completed_at && (
                  <div className="text-xs text-muted mt-0.5">
                    Completed {new Date(t.completed_at).toLocaleString()}
                  </div>
                )}
                <textarea
                  className="mt-2 w-full text-sm border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 disabled:opacity-60"
                  placeholder="Notes (optional)"
                  rows={2}
                  value={notesDraft[t.id] ?? ''}
                  disabled={!canEdit || busyId === t.id}
                  onChange={(e) =>
                    setNotesDraft((prev) => ({ ...prev, [t.id]: e.target.value }))
                  }
                  onBlur={() => {
                    if ((notesDraft[t.id] ?? '') !== (t.notes ?? '')) {
                      toggle(t, t.completed);
                    }
                  }}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
